import { useCallback, useEffect, useRef, useState } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apis from "services";
import { formatChatDate } from "utils/formatDate";
import SupportWhitelistPanel from "./SupportWhitelistPanel";

const statusLabel = (status) => ({
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
}[status] || status);

const ManageAllTickets = () => {
  const { lang } = useParams();
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({ search: "", status: "all", unread: false, page: 1, limit: 20 });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [investorSearch, setInvestorSearch] = useState("");
  const [investors, setInvestors] = useState([]);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [outreach, setOutreach] = useState({ userId: "", subject: "", message: "", priority: "low", category: "general" });
  const [outreachAttachment, setOutreachAttachment] = useState(null);
  const outreachFileInputRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showWhitelist, setShowWhitelist] = useState(false);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apis.getSupportConversations(filters);
      const data = response?.data?.data;
      setConversations(data?.conversations || []);
      setPagination({ total: data?.total || 0, totalPages: data?.totalPages || 0 });
    } catch (error) {
      toast.error(error.message || "Could not load support conversations");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!showNewConversation) return undefined;
    const timeout = setTimeout(async () => {
      try {
        const response = await apis.getAllUser({ search: investorSearch, page: 1, limit: 10 });
        const matches = response?.data?.users || [];
        setInvestors(matches);
        const exactEmail = matches.find((investor) =>
          investor.email?.toLowerCase() === investorSearch.trim().toLowerCase()
        );
        if (exactEmail) {
          setOutreach((current) => ({ ...current, userId: exactEmail._id }));
          setSelectedInvestor(exactEmail);
        }
      } catch {
        setInvestors([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [investorSearch, showNewConversation]);

  const startConversation = async (event) => {
    event.preventDefault();
    if (!outreach.userId) {
      toast.error("Search for and choose an investor first");
      return;
    }
    if (!outreach.subject.trim() || !outreach.message.trim()) return;
    try {
      const payload = new FormData();
      Object.entries(outreach).forEach(([key, value]) => payload.append(key, value));
      payload.append("clientMessageId", `admin-outreach-${Date.now()}`);
      if (outreachAttachment) payload.append("attachment", outreachAttachment);
      await apis.startSupportConversation(payload);
      setShowNewConversation(false);
      setOutreach({ userId: "", subject: "", message: "", priority: "low", category: "general" });
      setInvestorSearch("");
      setSelectedInvestor(null);
      setOutreachAttachment(null);
      if (outreachFileInputRef.current) outreachFileInputRef.current.value = "";
      fetchConversations();
      toast.success("Support conversation started");
    } catch (error) {
      toast.error(error.message || "Could not start the conversation");
    }
  };

  const deleteConversations = async ({ all = false } = {}) => {
    const countLabel = all ? "all support conversations" : `${selectedIds.length} selected conversation(s)`;
    if (!all && selectedIds.length === 0) return;
    if (!window.confirm(`Permanently delete ${countLabel}? This also removes the chats from investor accounts and deletes their attachments.`)) return;
    setDeleting(true);
    try {
      const response = await apis.bulkDeleteSupportConversations(all ? { all: true } : { ids: selectedIds });
      setSelectedIds([]);
      await fetchConversations();
      toast.success(response?.data?.message || "Support conversations deleted");
    } catch (error) {
      toast.error(error.message || "Could not delete support conversations");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters((current) => ({ ...current, search: searchInput.trim(), page: 1 }));
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const refresh = (event) => {
      const updated = event.detail;
      if (!updated?._id) return;
      if (updated.deleted) {
        setConversations((current) => current.filter((item) => item._id !== updated._id));
        setSelectedIds((current) => current.filter((id) => id !== updated._id));
        return;
      }
      setConversations((current) => {
        const exists = current.some((item) => item._id === updated._id);
        const next = exists
          ? current.map((item) => item._id === updated._id ? { ...item, ...updated, messages: undefined } : item)
          : [updated, ...current];
        return next.sort((a, b) => new Date(b.lastMessageAt || b.updatedAt) - new Date(a.lastMessageAt || a.updatedAt));
      });
    };
    window.addEventListener("support-chat-updated", refresh);
    return () => window.removeEventListener("support-chat-updated", refresh);
  }, []);

  return (
    <div className="tickets-tab-container support-inbox">
      <div className="support-inbox-header">
        <div>
          <h2>Support inbox</h2>
          <p>{pagination.total} conversations in the shared queue</p>
          <div className="support-header-actions">
            <button type="button" className="support-new-button" onClick={() => setShowNewConversation((value) => !value)}>
              {showNewConversation ? "Cancel" : "Start conversation"}
            </button>
            <button type="button" className="support-whitelist-button" onClick={() => setShowWhitelist((value) => !value)}>
              Support agents
            </button>
          </div>
        </div>
        <div className="support-filters">
          <label className="support-search">
            <IoSearchOutline />
            <input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search reference, investor or subject"
            />
          </label>
          <select
            value={filters.status}
            onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value, page: 1 }))}
          >
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <label className="support-unread-filter">
            <input
              type="checkbox"
              checked={filters.unread}
              onChange={(event) => setFilters((current) => ({ ...current, unread: event.target.checked, page: 1 }))}
            />
            Unread only
          </label>
        </div>
      </div>

      <SupportWhitelistPanel open={showWhitelist} onClose={() => setShowWhitelist(false)} />

      {showNewConversation && (
        <form className="support-outreach-form" onSubmit={startConversation}>
          <h3>Start a conversation with an investor</h3>
          <input
            type="search"
            value={investorSearch}
            onChange={(event) => {
              setInvestorSearch(event.target.value);
              setSelectedInvestor(null);
              setOutreach((current) => ({ ...current, userId: "" }));
            }}
            placeholder="Enter investor email or name"
            autoComplete="off"
          />
          {selectedInvestor ? (
            <div className="support-selected-investor">
              <span>Sending to <strong>{selectedInvestor.name}</strong> · {selectedInvestor.email}</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedInvestor(null);
                  setInvestorSearch("");
                  setOutreach((current) => ({ ...current, userId: "" }));
                }}
              >
                Change
              </button>
            </div>
          ) : investorSearch.trim() && (
            <div className="support-investor-results">
              {investors.length > 0 ? investors.map((investor) => (
                <button
                  type="button"
                  className="support-investor-result"
                  key={investor._id}
                  onClick={() => {
                    setSelectedInvestor(investor);
                    setInvestorSearch(investor.email || investor.name || "");
                    setOutreach((current) => ({ ...current, userId: investor._id }));
                  }}
                >
                  <strong>{investor.name || "Investor"}</strong>
                  <span>{investor.email}</span>
                </button>
              )) : <p>No matching investors found</p>}
            </div>
          )}
          <input required maxLength={160} value={outreach.subject} onChange={(event) => setOutreach((current) => ({ ...current, subject: event.target.value }))} placeholder="Subject" />
          <textarea required rows={3} value={outreach.message} onChange={(event) => setOutreach((current) => ({ ...current, message: event.target.value }))} placeholder="First message" />
          <select value={outreach.category} onChange={(event) => setOutreach((current) => ({ ...current, category: event.target.value }))}>
            <option value="general">General</option><option value="account">Account</option><option value="transactional">Transactional</option>
          </select>
          <select value={outreach.priority} onChange={(event) => setOutreach((current) => ({ ...current, priority: event.target.value }))}>
            <option value="low">Low priority</option><option value="medium">Medium priority</option><option value="high">High priority</option>
          </select>
          <div className="support-attachment-control">
            <input
              ref={outreachFileInputRef}
              className="support-native-file-input"
              type="file"
              aria-label="Attach file to new conversation"
              onChange={(event) => setOutreachAttachment(event.target.files?.[0] || null)}
            />
            {outreachAttachment && (
              <span className="support-selected-file">
                {outreachAttachment.name}
                <button
                  type="button"
                  aria-label="Remove attachment"
                  onClick={() => {
                    setOutreachAttachment(null);
                    if (outreachFileInputRef.current) outreachFileInputRef.current.value = "";
                  }}
                >
                  ×
                </button>
              </span>
            )}
          </div>
          <button type="submit">Start conversation</button>
        </form>
      )}

      <div className="support-bulk-actions">
        <span>{selectedIds.length} selected</span>
        <button type="button" disabled={deleting || selectedIds.length === 0} onClick={() => deleteConversations()}>
          Delete selected
        </button>
        <button type="button" className="support-delete-all" disabled={deleting || pagination.total === 0} onClick={() => deleteConversations({ all: true })}>
          Delete all chats
        </button>
      </div>

      <div className="ticket-table-wrapper">
        <table className="ticket-history-table">
          <thead>
            <tr className="tickets-table-head">
              <th className="support-select-column">
                <input
                  type="checkbox"
                  aria-label="Select all conversations on this page"
                  checked={conversations.length > 0 && conversations.every((item) => selectedIds.includes(item._id))}
                  onChange={(event) => {
                    const pageIds = conversations.map((item) => item._id);
                    setSelectedIds((current) => event.target.checked
                      ? [...new Set([...current, ...pageIds])]
                      : current.filter((id) => !pageIds.includes(id)));
                  }}
                />
              </th>
              <th>Conversation</th>
              <th>Investor</th>
              <th>Latest message</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><div className="loader" /></td></tr>
            ) : conversations.length === 0 ? (
              <tr><td colSpan={7}><p className="no-record-found">No conversations found</p></td></tr>
            ) : conversations.map((conversation) => (
              <tr key={conversation._id} className={conversation.unreadByAdmin > 0 ? "support-row-unread" : ""}>
                <td className="support-select-column">
                  <input
                    type="checkbox"
                    aria-label={`Select ${conversation.reference || conversation._id}`}
                    checked={selectedIds.includes(conversation._id)}
                    onChange={(event) => setSelectedIds((current) => event.target.checked
                      ? [...current, conversation._id]
                      : current.filter((id) => id !== conversation._id))}
                  />
                </td>
                <td>
                  <Link className="support-conversation-link" to={`/${lang}/admin/tickets/${conversation._id}`}>
                    <strong>{conversation.reference || conversation._id}</strong>
                    <span>{conversation.subject}</span>
                  </Link>
                </td>
                <td>
                  <strong>{conversation.userId?.name || "Investor"}</strong>
                  <small>{conversation.userId?.email}</small>
                </td>
                <td className="support-preview">
                  {conversation.latestMessagePreview || conversation.description || "No message preview"}
                  {conversation.unreadByAdmin > 0 && <b className="support-unread-count">{conversation.unreadByAdmin}</b>}
                </td>
                <td><span className={`custom-badge ${conversation.priority === "high" ? "red-badge" : conversation.priority === "medium" ? "orange-badge" : "green-badge"}`}>{conversation.priority}</span></td>
                <td><span className={`custom-badge ${conversation.status === "closed" || conversation.status === "resolved" ? "green-badge" : "orange-badge"}`}>{statusLabel(conversation.status)}</span></td>
                <td>{formatChatDate(conversation.lastMessageAt || conversation.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div>Total: {pagination.total}</div>
          <div>
            <button className="paginator" disabled={filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}><IoIosArrowBack /></button>
            Page {filters.page} of {pagination.totalPages}
            <button className="paginator" disabled={filters.page >= pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}><IoIosArrowForward /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAllTickets;
